import { downloadByLinkCreation } from './downloadByLinkCreation';
import { DcAppParentMapping } from '../../modules/mac/duck/api';

const formatISODateToDefault = (date: string | undefined) =>
  date ? date.split('T', 1)[0].replace('/', '-') : '';

const csvFormatter = (value) =>
  value === null || value === '' ? '""' : `"${String(value).replace(/"/g, "'")}"`;

const convertLicensesToCSVDefault = (data: any, partnerId: string) => {
  let str =
    'SEN, Product, Organization, Main Contact, Start Date, End Date, Last Transaction Date, Entitlement Id, Entitlement Number\r\n';

  data.forEach((item) => {
    const isMainContact = item.partnerId?.toString() === partnerId;
    const mainContact = isMainContact && item.mainContactEmail;

    const startDate = isMainContact && formatISODateToDefault(item.startDate);
    const endDate = isMainContact && formatISODateToDefault(item.expireDate);
    const lastTransactionDate = isMainContact && formatISODateToDefault(item.lastTransactionDate);

    const csvRow = `${csvFormatter(item.accountId)},${csvFormatter(
      item.productDescription,
    )},${csvFormatter(item.accountName)},${csvFormatter(mainContact)},${csvFormatter(
      startDate,
    )},${csvFormatter(endDate)},${csvFormatter(lastTransactionDate)},${csvFormatter(
      item.ccpEntitlementId,
    )},${csvFormatter(item.slug)}`;
    str += `${csvRow}\r\n`;
  });
  return str;
};

const convertLicensesToCSVMigration = (data: any) => {
  let str =
    'organisation, sen, name, site_or_org_id, main_technical_contact, product_sen, product, migration_date, entitlement_id, entitlement_number\r\n';

  data.forEach((item) => {
    const sen = item.parentAccountId && `SEN-${item.parentAccountId}`;
    const migrationDate = formatISODateToDefault(item.migrationDate);

    const csvRow = `${csvFormatter(item.mainContactOrganisationName)},${csvFormatter(
      sen,
    )},${csvFormatter(item.accountName)},${csvFormatter(item.externalUuid)},${csvFormatter(
      item.mainContactEmail,
    )},${csvFormatter(item.accountId)},${csvFormatter(item.productDescription)},${csvFormatter(
      migrationDate,
    )},${csvFormatter(item.ccpEntitlementId)},${csvFormatter(item.slug)}`;
    str += `${csvRow}\r\n`;
  });
  return str;
};

const convertProductOfferingKeyMappingToCSV = (data: any) => {
  let str =
    'old_product_key, old_external_sku_id, new_product_id, new_offering_id, description, vendor_name, publicly_available\n';

  data.forEach((item) => {
    const publiclyAvailable = item.publicly_available ? 'YES' : 'NO';

    const csvRow = `${csvFormatter(item.old_product_key)},${csvFormatter(
      item.old_external_sku_id,
    )},${csvFormatter(item.new_product_id)},${csvFormatter(item.new_offering_id)},${csvFormatter(
      item.description,
    )},${csvFormatter(item.vendor_name)},${publiclyAvailable}`;
    str += `${csvRow}\n`;
  });
  return str;
};

const downloadCSV = (csvData: any, filename: string) => {
  if (!csvData) return;

  const blob = new Blob([csvData], { type: 'text/csv' });
  const href = URL.createObjectURL(blob);
  const download = `${filename}.csv`;

  downloadByLinkCreation(href, download);
};

/**
 * Creates a link element with the file content as a base64 encoded string and the suggested filename.
 * Clicks the link to download the file and removes the link from the DOM.
 * @param response The response of fetched api.
 * @param isMigration The type of CSV to download.
 * @param partnerId The partner Id to check if it's main contact for CSV download.
 * @returns void
 */

export const downloadLicensesOverviewCSV = (
  response: any,
  isMigration: boolean,
  partnerId: string,
) => {
  if (!response) return;

  const convertToCSV = isMigration ? convertLicensesToCSVMigration : convertLicensesToCSVDefault;
  const filename = isMigration ? 'Migration overview' : 'Customer account overview';
  const csvData = convertToCSV(response, partnerId);
  downloadCSV(csvData, filename);
};

export const downloadProductOfferingKeyMappingsCSV = (response: any) => {
  if (!response) return;

  const csvData = convertProductOfferingKeyMappingToCSV(response);
  downloadCSV(csvData, 'product-offering-key-mappings');
};

/**
 * Converts DC App Parent Mapping data to CSV format and downloads it
 * CSV headers: app_account_id, parent_account_id
 * @param data - Array of DC App Parent Mapping objects
 * @param partnerId - The partner ID (used in filename)
 * @returns void
 */
const convertDcAppParentMappingToCSV = (data: DcAppParentMapping[]) => {
  let str = 'app_account_id,parent_account_id\r\n';

  data.forEach((item) => {
    const csvRow = `${csvFormatter(item.appAccountId)},${csvFormatter(item.parentAccountId)}`;
    str += `${csvRow}\r\n`;
  });

  return str;
};

export const downloadDcAppParentMappingCSV = (
  response: DcAppParentMapping[],
  partnerId: string,
) => {
  if (!response || !Array.isArray(response)) return;

  const csvData = convertDcAppParentMappingToCSV(response);
  const filename = `dc-app-parent-mapping-partner-${partnerId}`;
  downloadCSV(csvData, filename);
};

export const downloadFailedDcAppParentMappingCSV = (
  failedMappings: DcAppParentMapping[],
  partnerId: string,
) => {
  if (!failedMappings || !Array.isArray(failedMappings)) return;

  // Re-use the same CSV structure as the original download so the file
  // can be corrected and re-uploaded via the upload button.
  const csvData = convertDcAppParentMappingToCSV(failedMappings);
  const filename = `dc-app-parent-mapping-failed-partner-${partnerId}`;
  downloadCSV(csvData, filename);
};

// Strip surrounding quotes added by csvFormatter during download.
const parseCell = (cell: string): string => {
  const trimmed = cell.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/''/g, '"');
  }
  return trimmed;
};

// Split a CSV line on commas but respect quoted fields.
// The regex alternation produces an extra empty-string capture between
// adjacent quoted fields, so quoted values land at even indices (0, 2, 4…).
const splitCsvLine = (line: string): [string, string] => {
  const parts = line.match(/("(?:[^"])*"|[^,]*)/g) ?? [];
  return [parseCell(parts[0] ?? ''), parseCell(parts[2] ?? '')];
};

// Parse a raw parent account ID string — empty/null literal → null,
// invalid non-numeric → null (avoids pushing NaN into the result).
const parseParentAccountId = (raw: string): number | null => {
  if (raw === '' || raw === 'null') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Parses a CSV string (with headers app_account_id,parent_account_id) into
 * an array of DcAppParentMapping objects, skipping blank lines and the header row.
 * @param csvText - Raw CSV file content as a string
 * @returns Array of DcAppParentMapping objects
 */
export const parseDcAppParentMappingCSV = (csvText: string): DcAppParentMapping[] => {
  const lines = csvText.split(/\r?\n/);
  const result: DcAppParentMapping[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const [rawAppId, rawParentId] = splitCsvLine(line);
    const appAccountId = Number(rawAppId);

    if (!Number.isNaN(appAccountId) && appAccountId !== 0) {
      result.push({ appAccountId, parentAccountId: parseParentAccountId(rawParentId) });
    }
  }

  return result;
};
